import { ActivatedRoute, Params } from '@angular/router';
import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import { CustomerClientsClient, GetClient } from 'src/app/generated-client/generated-client';
import { Subject, combineLatest } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { AddClientDialogComponent } from '../add-client-dialog/add-client-dialog.component';
import { ClientsOfCustomerService } from 'src/app/services/clients-of-customer.service';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent, MatPaginator } from '@angular/material/paginator';
import { PaginationResult } from 'ng-lightquery';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { MatFormField, MatLabel, MatInput } from '@angular/material/input';
import { UnassignedClientTableComponent } from '../unassigned-client-table/unassigned-client-table.component';
import { MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'da-clients-of-customer',
    templateUrl: './clients-of-customer.component.html',
    styleUrl: './clients-of-customer.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButton, MatCheckbox, FormsModule, MatFormField, MatLabel, MatInput, UnassignedClientTableComponent, MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatSortHeader, MatCellDef, MatCell, MatIconButton, MatIcon, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatPaginator]
})
export class ClientsOfCustomerComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  clientsOfCustomerService = inject(ClientsOfCustomerService);
  private customerClientsClient = inject(CustomerClientsClient);

  private $filter = new Subject<string>();
  private $destroy = new Subject<boolean>();
  displayedColumns = ['id', 'name', 'customerName', 'action'];
  isUnassignedClients = signal(false);
  customerId = signal<string>(null);
  clientsOfCustomerPaginated = signal<PaginationResult<GetClient>>(null);

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.$destroy)).subscribe((params: Params) => {
      this.customerId.set(params['customerId']);
      if (this.customerId()) {
        this.clientsOfCustomerService.setQueryParameter('customerId', this.customerId() || null);
      }
    });

    this.$filter.pipe(takeUntil(this.$destroy), debounceTime(250)).subscribe((filterWord: string) => this.applyFilter(filterWord));

    this.clientsOfCustomerService.paginationResult.pipe(takeUntil(this.$destroy)).subscribe(r => this.clientsOfCustomerPaginated.set(r));
  }

  ngOnDestroy(): void {
    this.clientsOfCustomerService.setQueryParameter('filter', null);
    this.clientsOfCustomerService.setQueryParameter('customerId', null);
    this.$destroy.next(true);
    this.$destroy.complete();
  }

  filter(filterWord: string): void {
    this.$filter.next(filterWord);
  }

  applyFilter(filterWord: string): void {
    this.clientsOfCustomerService.setQueryParameter('filter', filterWord.trim().toLowerCase() || null);
  }

  addClient(): void {
    this.dialog
      .open(AddClientDialogComponent)
      .afterClosed()
      .subscribe((addingClients: GetClient[]) => {
        if (addingClients?.length) {
          const addingClientsRequest = addingClients.map((client: GetClient) =>
            this.customerClientsClient.assignClientToCustomer(this.customerId(), client.clientId)
          );
          combineLatest(addingClientsRequest).subscribe({
            next: () => this.refresh(),
            error: err => console.error(err)
          });
        }
      });
  }

  refresh(): void {
    this.clientsOfCustomerService.forceRefresh();
  }

  updateUnassignedClients(): void {
    if (!this.isUnassignedClients()) {
      this.refresh();
    }
  }

  removeClient(event: MouseEvent, row: GetClient): void {
    event.stopPropagation();
    this.dialog
      .open(ConfirmDialogComponent, { data: { typeWord: 'client', name: row.name } })
      .afterClosed()
      .subscribe(isRemove => {
        if (isRemove) {
          this.customerClientsClient.removeClientFromCustomer(this.customerId(), row.clientId).subscribe({
            next: () => this.refresh(),
            error: err => console.error(err)
          });
        }
      });
  }

  onPage(pageEvent: PageEvent) {
    this.clientsOfCustomerService.page = pageEvent.pageIndex + 1;
    this.clientsOfCustomerService.pageSize = pageEvent.pageSize;
  }

  onSort(event: { active: string; direction: string }) {
    if (!event.direction) {
      this.clientsOfCustomerService.sort = null;
    } else {
      this.clientsOfCustomerService.sort = { propertyName: event.active, isDescending: event.direction === 'desc' };
    }
  }
}
